import type { ZplPrinterListItem } from "./zplPrinterDisplay";

export type FloorTabletMobility = "mobile" | "stationary";

export type FloorPrinterScope = {
  userId: string;
  deviceId: string;
  mobility: FloorTabletMobility;
};

export type FloorPrinterReadOptions = FloorPrinterScope & {
  fallback?: string;
  preferLabelSize?: string;
  preferDpi?: number;
  printers?: ZplPrinterListItem[];
};

const SCOPED_STORAGE_PREFIX = "sgs-zpl-printer:v1:";

export function readFloorDeviceId(): string {
  if (typeof window === "undefined") {
    return "";
  }
  const fromBridge = window.SgsLauncher?.getDeviceId?.();
  if (typeof fromBridge === "string" && fromBridge.trim() !== "") {
    return fromBridge.trim();
  }
  const injected = window.__sgsDeviceId;
  return typeof injected === "string" ? injected.trim() : "";
}

export function readFloorTabletMobility(): FloorTabletMobility {
  if (typeof window === "undefined") {
    return "mobile";
  }
  const fromBridge = window.SgsLauncher?.getTabletMobility?.();
  const injected = window.__sgsTabletMobility;
  const value = fromBridge ?? injected;
  return value === "stationary" ? "stationary" : "mobile";
}

export function buildScopedPrinterStorageKey(
  workflowKey: string,
  scope: Pick<FloorPrinterScope, "userId" | "deviceId">,
): string {
  const user = scope.userId.trim() || "anonymous";
  const device = scope.deviceId.trim() || "browser";
  return `${SCOPED_STORAGE_PREFIX}${user}:${device}:${workflowKey}`;
}

export function readLauncherPrinterPref(workflowKey: string): string | null {
  const value = window.SgsLauncher?.readPrinterPref?.(workflowKey);
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function writeLauncherPrinterPref(workflowKey: string, printer: string): void {
  const trimmed = printer.trim();
  if (trimmed === "") {
    return;
  }
  window.SgsLauncher?.writePrinterPref?.(workflowKey, trimmed);
}

export function pickStationaryPrinter(
  printers: ZplPrinterListItem[],
  options: { preferLabelSize?: string; preferDpi?: number },
): string | null {
  if (printers.length === 0) {
    return null;
  }
  const preferLabelSize = options.preferLabelSize?.trim().toLowerCase();
  const matches = printers.filter((printerRow) => {
    if (preferLabelSize != null && preferLabelSize !== "") {
      const labelSize = printerRow.labelSize?.trim().toLowerCase();
      if (labelSize !== preferLabelSize) {
        return false;
      }
    }
    if (options.preferDpi != null && printerRow.dpi !== options.preferDpi) {
      return false;
    }
    return true;
  });
  return (matches[0] ?? printers[0])?.name ?? null;
}

function launcherPrinterBridgePresent(): boolean {
  return typeof window !== "undefined" && typeof window.SgsLauncher?.readPrinterPref === "function";
}

/**
 * One preference per hub user, device, and page.
 * The tablet bridge stores that key outside the WebView. A browser stores the same key in localStorage.
 */
export function readScopedZplPrinter(
  workflowKey: string,
  options: FloorPrinterReadOptions,
): string {
  const fallback = options.fallback?.trim() || "";

  if (launcherPrinterBridgePresent()) {
    const launcherValue = readLauncherPrinterPref(workflowKey);
    if (launcherValue != null) {
      return launcherValue;
    }
  } else if (typeof window !== "undefined") {
    const scopedKey = buildScopedPrinterStorageKey(workflowKey, options);
    const scopedValue = window.localStorage.getItem(scopedKey)?.trim();
    if (scopedValue != null && scopedValue !== "") {
      return scopedValue;
    }
    const legacyValue = window.localStorage.getItem(workflowKey)?.trim();
    if (legacyValue != null && legacyValue !== "") {
      window.localStorage.setItem(scopedKey, legacyValue);
      window.localStorage.removeItem(workflowKey);
      return legacyValue;
    }
  }

  if (
    options.mobility === "stationary" &&
    options.printers != null &&
    options.printers.length > 0
  ) {
    const stationaryDefault = pickStationaryPrinter(options.printers, {
      preferLabelSize: options.preferLabelSize,
      preferDpi: options.preferDpi,
    });
    if (stationaryDefault != null) {
      return stationaryDefault;
    }
  }

  return fallback;
}

export function writeScopedZplPrinter(
  workflowKey: string,
  printer: string,
  scope: Pick<FloorPrinterScope, "userId" | "deviceId">,
): void {
  const trimmed = printer.trim();
  if (trimmed === "") {
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  if (launcherPrinterBridgePresent()) {
    writeLauncherPrinterPref(workflowKey, trimmed);
    return;
  }

  const scopedKey = buildScopedPrinterStorageKey(workflowKey, scope);
  window.localStorage.setItem(scopedKey, trimmed);
  window.localStorage.removeItem(workflowKey);
}
